// Schedule — TanStack Query hooks.
// Query keys включают tenantId (см. docs/stack/07-frontend-vite.md §4) + фильтры.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

// Cross-feature: карточка абонемента открывается поверх «Рабочего дня» и после
// отметки визита обязана освежиться (план 139 Ф1).
import { subscriptionsKeys } from '@/features/subscriptions/hooks/useSubscriptions';
import { branchParamsObject } from '@/lib/branch-params';
import { branchQueryKey, useBranchIds, useTenantId } from '@/lib/tenant-context';

import {
  attachSubscriptionToAttendance,
  cancelFutureOccurrences,
  cancelOccurrence,
  deleteOccurrence,
  deleteSeriesChain,
  type DeleteChainResult,
  createGroup,
  createOccurrence,
  createSeries,
  createSeriesBatch,
  editOccurrence,
  editSeries,
  enrollClient,
  getOccurrence,
  getRosterLayout,
  getScheduleSettings,
  listEnrollments,
  listGroups,
  listOccurrenceCandidates,
  listRosterGroups,
  listHalls,
  getGroupRoster,
  listOccurrences,
  listRentals,
  listTeachers,
  markOccurrenceNotHeld,
  markRosterPrinted,
  revertOccurrenceNotHeld,
  saveRosterLayout,
  markVisit,
  markVisitDirect,
  moveOccurrence,
  notifyOccurrenceChange,
  putScheduleSettings,
  type CancelFutureBody,
  type CancelFutureResult,
  type CancelOccurrenceBody,
  type ClassType,
  type CreateGroupBody,
  type CreateGroupResult,
  type CreateOccurrenceBody,
  type CreateOccurrenceResult,
  type CreateSeriesBody,
  type CreateSeriesResult,
  type EditOccurrenceBody,
  type EditSeriesBody,
  type EnrollClientBody,
  type EnrollClientResult,
  type EnrollmentStatus,
  type GroupListItem,
  type HallItem,
  type ListOccurrencesParams,
  type ListOccurrencesResult,
  type MarkNotHeldBody,
  type MarkNotHeldResult,
  type MoveOccurrenceBody,
  type MoveOccurrenceResult,
  type NotificationLevel,
  type NotifyOccurrenceChangeOpts,
  type NotifyOccurrenceChangeResult,
  type OccurrenceChangeNotifyKind,
  type OccurrenceCandidate,
  type RosterColumn,
  type RosterGroupItem,
  type RosterLayout,
  type OccurrenceEnrollment,
  type GroupRoster,
  type OccurrenceItem,
  type RentalItem,
  type RevertNotHeldResult,
  type ScheduleSettings,
  type CreateSeriesBatchResult,
  type TeacherItem,
} from '../api/schedule.api';

/**
 * Фильтры сетки и окна записи.
 *
 * `branchIds` — МАССИВ (план 106, мультивыбор в шапке). Имя обязано совпадать с
 * `ListOccurrencesParams`: сборщик query читает поле по имени, а лишнее
 * свойство, пришедшее через spread, TypeScript не ловит — так фильтр локации и
 * пропал молча (гоча §52).
 */
export interface OccurrencesFilters {
  from: string;
  to: string;
  branchIds?: string[];
  groupId?: string;
  teacherId?: string;
  hallId?: string;
  directionId?: string;
  /** Категория групп — фильтр окна записи (план 104 Б2). */
  categoryId?: string;
  classType?: ClassType;
}

export interface RentalsFilters {
  from: string;
  to: string;
  branchIds?: string[];
}

export function scheduleKeys(tenantId: string | null) {
  const root = ['schedule', tenantId] as const;
  return {
    root,
    groups: [...root, 'groups'] as const,
    halls: (branchIds?: string[]) => [...root, 'halls', branchQueryKey(branchIds ?? [])] as const,
    teachers: [...root, 'teachers'] as const,
    occurrences: (filters: OccurrencesFilters) => [...root, 'occurrences', filters] as const,
    poster: (filters: OccurrencesFilters) => [...root, 'poster', filters] as const,
    occurrence: (id: string) => [...root, 'occurrence', id] as const,
    rentals: (filters: RentalsFilters) => [...root, 'rentals', filters] as const,
    settings: [...root, 'settings'] as const,
    enrollments: (occurrenceId: string) => [...root, 'enrollments', occurrenceId] as const,
    roster: (groupId: string, month: string) => [...root, 'roster', groupId, month] as const,
    candidates: (occurrenceId: string) => [...root, 'candidates', occurrenceId] as const,
    rosterGroups: (month: string) => [...root, 'roster-groups', month] as const,
    rosterLayout: [...root, 'roster-layout'] as const,
  };
}

/**
 * Сброс всех экранов, где видна отметка посещения (план 139 Ф1).
 *
 * ЗАЧЕМ ОБЩИЙ ПОМОЩНИК. Отмечают в двух местах — карточка занятия в
 * «Расписании» и «Рабочий день», — а мутации у них общие. Поверх «Рабочего дня»
 * при этом открываются карточка клиента и карточка абонемента (план 128), и они
 * жили своей жизнью: отметил визит — «Посещения» в карточке клиента ещё
 * полминуты показывали прежний список, а списанное занятие не появлялось в
 * карточке абонемента.
 *
 * Ключи:
 *  - состав и кандидаты занятия — точные ключи, чтобы правая панель «Рабочего
 *    дня» обновилась даже если префикс расписания когда-нибудь сузят;
 *  - ГОЛЫЙ префикс `['schedule']`, а не `scheduleKeys(tenantId).root`: часть
 *    ключей расписания строится от СОСТАВНОГО tenant — `useGroups` и
 *    `useRosterGroups` кладут туда `tenant:branches`, и под `['schedule',
 *    tenantId]` такие ключи не попадают. Образец — `useMarkRosterPrinted`;
 *  - `['clients', tenantId, 'attendance-filtered']` — «Посещения» карточки
 *    клиента (`useClientAttendanceFiltered`); фильтров там много, поэтому
 *    префикс, а не конкретный ключ;
 *  - `subscriptionsKeys(tenantId).root` — карточка абонемента и списки
 *    абонементов клиента. Ключа `['subscription']` в единственном числе в
 *    админке нет вовсе (проверено 09.09.2026).
 */
export function invalidateAttendanceViews(
  queryClient: QueryClient,
  tenantId: string | null,
  occurrenceId: string | null,
): void {
  const keys = scheduleKeys(tenantId);
  if (occurrenceId) {
    void queryClient.invalidateQueries({ queryKey: keys.enrollments(occurrenceId) });
    void queryClient.invalidateQueries({ queryKey: keys.candidates(occurrenceId) });
  }
  void queryClient.invalidateQueries({ queryKey: ['schedule'] });
  void queryClient.invalidateQueries({ queryKey: ['clients', tenantId, 'attendance-filtered'] });
  void queryClient.invalidateQueries({ queryKey: subscriptionsKeys(tenantId).root });
}

/**
 * Группы для выпадашек расписания — ФИЛИАЛА ИЗ ШАПКИ.
 *
 * Филиал участвует и в ключе кэша: иначе при переключении шапки показывался бы
 * список предыдущего филиала. Группы, у которых филиал не задан вовсе, сервер
 * отдаёт в любом филиале — прятать их нельзя, у части из них сотни занятий.
 */
export function useGroups(): UseQueryResult<GroupListItem[], Error> {
  const tenantId = useTenantId();
  const branchIds = useBranchIds();
  // Ключ кэша канонизируем (план 106): один и тот же набор, отмеченный в
  // разном порядке, иначе даёт разные ключи и дубли запросов.
  const keys = scheduleKeys(`${tenantId}:${branchQueryKey(branchIds)}`);

  return useQuery({
    queryKey: keys.groups,
    queryFn: async () => (await listGroups(branchParamsObject(branchIds))).items,
    staleTime: 5 * 60_000, // группы меняются нечасто.
  });
}

export function useHalls(branchIds?: string[]): UseQueryResult<HallItem[], Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.halls(branchIds),
    queryFn: async () => (await listHalls(branchIds)).items,
    staleTime: 5 * 60_000, // залы меняются редко.
  });
}

export function useTeachers(branchIds?: string[]): UseQueryResult<TeacherItem[], Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    // Филиалы включаем в ключ — кэш per-выбор (без них — общий список).
    queryKey: [...keys.teachers, branchQueryKey(branchIds ?? [])],
    queryFn: async () => (await listTeachers(branchIds)).items,
    staleTime: 5 * 60_000, // педагоги меняются редко.
  });
}

export function useOccurrences(
  filters: OccurrencesFilters,
): UseQueryResult<ListOccurrencesResult, Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.occurrences(filters),
    queryFn: () =>
      listOccurrences({
        from: filters.from,
        to: filters.to,
        limit: 200,
        ...(filters.branchIds?.length ? { branchIds: filters.branchIds } : {}),
        ...(filters.groupId ? { groupId: filters.groupId } : {}),
        ...(filters.teacherId ? { teacherId: filters.teacherId } : {}),
        ...(filters.hallId ? { hallId: filters.hallId } : {}),
        ...(filters.directionId ? { directionId: filters.directionId } : {}),
        ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
        ...(filters.classType ? { classType: filters.classType } : {}),
      } satisfies ListOccurrencesParams),
  });
}

/** Сколько страниц по 200 дочитывает афиша, прежде чем сдаться. */
const POSTER_MAX_PAGES = 10;

export interface PosterOccurrences {
  items: OccurrenceItem[];
  /** Упёрлись в потолок страниц — часть занятий в лист не попала. */
  truncated: boolean;
}

/**
 * Занятия для афиши — ВСЕ, а не первая страница (план 109).
 *
 * `useOccurrences` берёт `limit: 200` (это и есть `MAX_LIMIT` сервера) и
 * `hasMore` не смотрит вовсе: сетке хватает, потому что администратор видит
 * день или неделю одного филиала. Афишу же печатают и вешают на стену — лист,
 * молча потерявший хвост недели, хуже отсутствия листа. Поэтому здесь идём по
 * курсору до конца.
 *
 * Курсор keyset-овый по (scheduled_at, id), сортировка стабильная, поэтому
 * страницы не «дрейфуют» между запросами. Потолок в 10 страниц — предохранитель
 * от бесконечного цикла; упёрлись — честно говорим об этом (`truncated`), а не
 * показываем обрезанный лист как полный.
 *
 * Ленивый: пока модалка закрыта (`enabled: false`), запросов нет.
 */
/** Ведомость группы на месяц (план 121 Ф1). Грузится при открытии модалки. */
export function useGroupRoster(
  groupId: string | null,
  month: string,
  enabled: boolean,
): UseQueryResult<GroupRoster, Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);
  return useQuery({
    queryKey: keys.roster(groupId ?? '-', month),
    enabled: enabled && Boolean(groupId),
    queryFn: () => getGroupRoster(groupId as string, month),
    staleTime: 60_000,
  });
}

/**
 * Группы для выбора ведомости: педагог, зал, занятия месяца, отметка о печати
 * (план 121, правка 01.09). Ключ включает месяц и филиалы — от них зависит всё
 * содержимое списка.
 */
export function useRosterGroups(
  month: string,
  enabled: boolean,
): UseQueryResult<RosterGroupItem[], Error> {
  const tenantId = useTenantId();
  const branchIds = useBranchIds();
  const keys = scheduleKeys(`${tenantId}:${branchQueryKey(branchIds)}`);
  return useQuery({
    queryKey: keys.rosterGroups(month),
    enabled,
    queryFn: () => listRosterGroups({ month, ...(branchIds.length ? { branchIds } : {}) }),
    staleTime: 60_000,
  });
}

/** Отметка «ведомость распечатана» — обновляет список, отметку видят все. */
export function useMarkRosterPrinted(): UseMutationResult<
  { group_id: string; month: string; printed_at: string },
  Error,
  { groupId: string; month: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, month }) => markRosterPrinted(groupId, month),
    onSuccess: () => {
      // Ключ списка зависит от филиалов шапки — инвалидируем по префиксу
      // расписания, а не по конкретному ключу.
      void queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });
}

/** Вид печатной ведомости — общий для всей школы (план 121, правка 01.09). */
export function useRosterLayout(enabled: boolean): UseQueryResult<RosterLayout, Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);
  return useQuery({
    queryKey: keys.rosterLayout,
    enabled,
    queryFn: () => getRosterLayout(),
    staleTime: 5 * 60_000,
  });
}

export function useSaveRosterLayout(): UseMutationResult<
  RosterLayout,
  Error,
  { columns: RosterColumn[]; logo_data: string | null }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);
  return useMutation({
    mutationFn: (body) => saveRosterLayout(body),
    onSuccess: (saved) => {
      // Кладём ответ в кэш сразу: лист перерисовывается тем же, что сохранили.
      queryClient.setQueryData(keys.rosterLayout, saved);
    },
  });
}

export function usePosterOccurrences(
  filters: OccurrencesFilters,
  enabled: boolean,
): UseQueryResult<PosterOccurrences, Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.poster(filters),
    enabled,
    queryFn: async () => {
      const items: OccurrenceItem[] = [];
      let cursor: string | undefined;
      let truncated = false;

      for (let page = 0; page < POSTER_MAX_PAGES; page += 1) {
        const res = await listOccurrences({
          from: filters.from,
          to: filters.to,
          limit: 200,
          ...(cursor ? { cursor } : {}),
          ...(filters.branchIds?.length ? { branchIds: filters.branchIds } : {}),
          ...(filters.groupId ? { groupId: filters.groupId } : {}),
          ...(filters.teacherId ? { teacherId: filters.teacherId } : {}),
          ...(filters.hallId ? { hallId: filters.hallId } : {}),
          ...(filters.directionId ? { directionId: filters.directionId } : {}),
          ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
          ...(filters.classType ? { classType: filters.classType } : {}),
        } satisfies ListOccurrencesParams);
        items.push(...res.items);
        if (!res.hasMore || !res.nextCursor) break;
        cursor = res.nextCursor;
        // Последняя итерация цикла, а сервер обещает продолжение — значит лист
        // неполон.
        if (page === POSTER_MAX_PAGES - 1) truncated = true;
      }

      return { items, truncated };
    },
  });
}

/**
 * Одно занятие по id — для попапа-карточки занятия из других разделов
 * (клик по посещению в карточке абонемента → OccurrenceDrawer). Ленивый:
 * запрос идёт только когда id задан (enabled).
 */
export function useOccurrence(id: string | null): UseQueryResult<OccurrenceItem, Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.occurrence(id ?? ''),
    queryFn: () => getOccurrence(id as string),
    enabled: !!id,
  });
}

export function useRentals(filters: RentalsFilters | null): UseQueryResult<RentalItem[], Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.rentals(filters ?? { from: '', to: '' }),
    queryFn: () =>
      listRentals({
        from: (filters as RentalsFilters).from,
        to: (filters as RentalsFilters).to,
        ...(filters?.branchIds?.length ? { branchIds: filters.branchIds } : {}),
      }),
    enabled: Boolean(filters),
  });
}

export function useScheduleSettings(): UseQueryResult<ScheduleSettings, Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.settings,
    queryFn: () => getScheduleSettings(),
    staleTime: 5 * 60_000, // настройки сетки меняются редко.
  });
}

export function useUpdateScheduleSettings(): UseMutationResult<
  ScheduleSettings,
  Error,
  ScheduleSettings
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: (body: ScheduleSettings) => putScheduleSettings(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

export function useCreateGroup(): UseMutationResult<CreateGroupResult, Error, CreateGroupBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: (body: CreateGroupBody) => createGroup(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useCreateSeries(): UseMutationResult<CreateSeriesResult, Error, CreateSeriesBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: (body: CreateSeriesBody) => createSeries(body),
    onSuccess: () => {
      // Новая серия генерирует occurrences — инвалидируем расписание целиком.
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/**
 * Пакетное создание серий — по одной на день недели (A1.2).
 * Каждый item имеет форму {@link CreateSeriesBody}; инвалидирует расписание целиком.
 */
export function useCreateSeriesBatch(): UseMutationResult<
  CreateSeriesBatchResult,
  Error,
  CreateSeriesBody[]
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: (items: CreateSeriesBody[]) => createSeriesBatch(items),
    onSuccess: () => {
      // Новые серии генерируют occurrences — инвалидируем расписание целиком.
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/**
 * Создание одного (разового) занятия — individual/rental/self_training.
 * Инвалидирует расписание целиком (как useCreateSeriesBatch).
 */
export function useCreateOccurrence(): UseMutationResult<
  CreateOccurrenceResult,
  Error,
  CreateOccurrenceBody
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: (body: CreateOccurrenceBody) => createOccurrence(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// ---------------------------------------------------------------------------
// Карточка занятия — записанные клиенты / отметка визита / запись клиента.
// ---------------------------------------------------------------------------

export function useEnrollments(
  occurrenceId: string | null,
): UseQueryResult<OccurrenceEnrollment[], Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.enrollments(occurrenceId ?? ''),
    queryFn: () => listEnrollments(occurrenceId as string),
    enabled: Boolean(occurrenceId),
    staleTime: 10_000,
  });
}

/**
 * Кандидаты на занятие для «Рабочего дня» (план 121 Ф2). Ключ отдельный от
 * записанных: списки обновляются вместе, но приходят разными запросами.
 */
export function useOccurrenceCandidates(
  occurrenceId: string | null,
): UseQueryResult<OccurrenceCandidate[], Error> {
  const tenantId = useTenantId();
  const keys = scheduleKeys(tenantId);

  return useQuery({
    queryKey: keys.candidates(occurrenceId ?? ''),
    queryFn: () => listOccurrenceCandidates(occurrenceId as string),
    enabled: Boolean(occurrenceId),
    staleTime: 10_000,
  });
}

/**
 * Отметка «пришёл» тому, кто не записан: сервер сам заводит запись и списывает
 * занятие. Инвалидируем оба списка — человек переезжает из кандидатов в состав.
 */
export function useMarkVisitDirect(
  occurrenceId: string | null,
): UseMutationResult<unknown, Error, { clientId: string }> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId }) => markVisitDirect(clientId, occurrenceId as string),
    onSuccess: () => {
      // Списки занятия (состав и кандидаты) сбрасывает общий помощник по
      // префиксу `['schedule']` — вместе с сеткой, карточкой клиента и
      // карточкой абонемента поверх «Рабочего дня» (план 139 Ф1).
      invalidateAttendanceViews(queryClient, tenantId, occurrenceId);
    },
  });
}

/** Привязка проданного абонемента к записи «без услуги» (карточка занятия). */
export function useAttachSubscription(
  occurrenceId: string | null,
): UseMutationResult<
  { subscription_id: string },
  Error,
  { attendanceId: string; subscriptionId: string }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ attendanceId, subscriptionId }) =>
      attachSubscriptionToAttendance(attendanceId, subscriptionId),
    onSuccess: () => {
      // Раньше сбрасывался только состав занятия: привязанный абонемент
      // менялся и в карточке абонемента, но там висели прежние цифры.
      invalidateAttendanceViews(queryClient, tenantId, occurrenceId);
    },
  });
}

export function useMarkVisit(
  occurrenceId: string | null,
): UseMutationResult<
  unknown,
  Error,
  { attendanceId: string; status: EnrollmentStatus; confirmLateCancel?: boolean }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attendanceId,
      status,
      confirmLateCancel,
    }: {
      attendanceId: string;
      status: EnrollmentStatus;
      confirmLateCancel?: boolean;
    }) => markVisit(attendanceId, status, confirmLateCancel === true),
    onSuccess: () => {
      // D8: сетка расписания показывает счётчик пришедших/остаток — освежаем её,
      // иначе списание и «пришло N» не видны без ручного F5. План 139 Ф1: тем же
      // помощником освежаются карточки клиента и абонемента поверх экрана.
      invalidateAttendanceViews(queryClient, tenantId, occurrenceId);
    },
  });
}

/**
 * «Остальные не пришли» — перевод всех неотмеченных записей занятия в `no_show`
 * (план 139 Ф3).
 *
 * БЕЗ БАТЧ-ЭНДПОИНТА, НАМЕРЕННО. По проду за 30 дней неотмеченные остались у 24
 * прошедших занятий из 257, максимум — четыре строки на занятие. Четыре
 * последовательных запроса не стоят новой ручки, новой строки в каталоге прав,
 * своей DTO и своего набора API-тестов. Вернёмся к батчу, если появятся занятия
 * с десятками неотмеченных.
 *
 * ПОСЛЕДОВАТЕЛЬНО И С ОСТАНОВКОЙ НА ПЕРВОЙ ОШИБКЕ. Самый вероятный отказ —
 * 403 `correction.shift_closed`: администратору без открытой смены филиала
 * вчерашние записи править нельзя (`edit-window-policy`). Это штатный ответ, а
 * не сбой, и он обязан оказаться на экране: молчащая кнопка уже стоила разбора
 * 04.09.2026 (гоча «непойманный 409»).
 *
 * `no_show` занятие НЕ списывает (`isChargingAttendanceStatus` знает только
 * `attended` и `no_show_charged`), клиенту ничего не отправляет — постановка в
 * `messenger_outbox` идёт строго под `cancelled`. Денег эта кнопка не касается.
 *
 * Сброс кэша — в `onSettled`, а не в `onSuccess`: строки, прошедшие до ошибки,
 * уже переведены, и экран обязан их показать.
 */
export function useMarkRestNoShow(
  occurrenceId: string | null,
): UseMutationResult<{ marked: number }, Error, { attendanceIds: string[] }> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ attendanceIds }) => {
      let marked = 0;
      for (const attendanceId of attendanceIds) {
        // Ошибку не глотаем — она останавливает цикл и уходит в onError.
        await markVisit(attendanceId, 'no_show');
        marked += 1;
      }
      return { marked };
    },
    onSettled: () => {
      invalidateAttendanceViews(queryClient, tenantId, occurrenceId);
    },
  });
}

export function useEnrollClient(
  occurrenceId: string | null,
): UseMutationResult<EnrollClientResult, Error, EnrollClientBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: EnrollClientBody) => enrollClient(body),
    onSuccess: () => {
      // Запись меняет число записанных в занятии — обновляем сетку, состав и
      // «Посещения» карточки клиента (план 139 Ф1).
      invalidateAttendanceViews(queryClient, tenantId, occurrenceId);
    },
  });
}

// ---------------------------------------------------------------------------
// Изменение в расписании (A1.3) — правка одного занятия или периода.
// Все мутации инвалидируют расписание целиком (keys.root): меняются
// occurrences под разными фильтрами, точечная инвалидация ненадёжна.
// ---------------------------------------------------------------------------

/** Правка одного занятия (occurrence): педагог/зал/ёмкость/группа на один день. */
export function useEditOccurrence(): UseMutationResult<
  unknown,
  Error,
  { id: string; body: EditOccurrenceBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EditOccurrenceBody }) =>
      editOccurrence(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/** Правка периода серии (с указанной даты): педагог/зал/ёмкость/группа. */
export function useEditSeries(): UseMutationResult<
  unknown,
  Error,
  { seriesId: string; body: EditSeriesBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: ({ seriesId, body }: { seriesId: string; body: EditSeriesBody }) =>
      editSeries(seriesId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/** Отмена будущих занятий серии — с указанной даты (или всех будущих). */
export function useCancelFuture(): UseMutationResult<
  CancelFutureResult,
  Error,
  { seriesId: string; body: CancelFutureBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: ({ seriesId, body }: { seriesId: string; body: CancelFutureBody }) =>
      cancelFutureOccurrences(seriesId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/** Удаление одного занятия (тестовое/ошибочное). Проведённое — 409 на бэке. */
export function useDeleteOccurrence(): UseMutationResult<unknown, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: (id: string) => deleteOccurrence(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/** Удаление всей цепочки (серии): непроведённые занятия + серия. */
export function useDeleteSeriesChain(): UseMutationResult<DeleteChainResult, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: (seriesId: string) => deleteSeriesChain(seriesId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/** Отмена одного занятия (occurrence) — опциональная причина. */
export function useCancelOccurrence(): UseMutationResult<
  unknown,
  Error,
  { id: string; body: CancelOccurrenceBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CancelOccurrenceBody }) =>
      cancelOccurrence(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/**
 * «Не состоялось» с причиной (план 153). Меняет и занятие, и отметки состава
 * (прогулы, списания), поэтому сбрасываем не только расписание, но и состав,
 * карточки клиента и абонемента — `invalidateAttendanceViews`.
 */
export function useMarkNotHeld(): UseMutationResult<
  MarkNotHeldResult,
  Error,
  { id: string; body: MarkNotHeldBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MarkNotHeldBody }) =>
      markOccurrenceNotHeld(id, body),
    onSuccess: (_res, { id }) => {
      invalidateAttendanceViews(queryClient, tenantId, id);
    },
  });
}

/** «Занятие было» — возврат из «Не состоялось» (план 153 §2.7). */
export function useRevertNotHeld(): UseMutationResult<RevertNotHeldResult, Error, { id: string }> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => revertOccurrenceNotHeld(id),
    onSuccess: (_res, { id }) => {
      invalidateAttendanceViews(queryClient, tenantId, id);
    },
  });
}

/** Перенос одного занятия — новое время + опционально зал/педагог. */
export function useMoveOccurrence(): UseMutationResult<
  MoveOccurrenceResult,
  Error,
  { id: string; body: MoveOccurrenceBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = scheduleKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MoveOccurrenceBody }) =>
      moveOccurrence(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/**
 * Уведомить клиентов об изменении в расписании (рассылка в диалоги).
 * `notificationLevel` — записанным ('enrolled', дефолт бэка) или всем клиентам
 * филиала ('branch'); `note` — клиент-видимое примечание, добавляется в текст.
 * Расписание не меняет — инвалидация кэша не нужна.
 */
export function useNotifyOccurrenceChange(): UseMutationResult<
  NotifyOccurrenceChangeResult,
  Error,
  {
    occurrenceId: string;
    changeKind: OccurrenceChangeNotifyKind;
    notificationLevel?: NotificationLevel;
    note?: string;
  }
> {
  return useMutation({
    mutationFn: ({
      occurrenceId,
      changeKind,
      notificationLevel,
      note,
    }: {
      occurrenceId: string;
      changeKind: OccurrenceChangeNotifyKind;
      notificationLevel?: NotificationLevel;
      note?: string;
    }) => {
      const opts: NotifyOccurrenceChangeOpts = {};
      if (notificationLevel) opts.notification_level = notificationLevel;
      if (note) opts.note = note;
      return notifyOccurrenceChange(occurrenceId, changeKind, opts);
    },
  });
}
